import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CategoryKind,
  PlannedMovementKind,
  RecurringSourceKind,
  type PlannedMovement,
} from '@financial/shared';
import { useAccounts } from '../accounts/useAccounts';
import { useCategories } from '../categories/useCategories';
import { useCreatePlanned, useUpdatePlanned } from './usePlanned';
import { getApiErrorMessage } from '../../lib/errors';
import { toDateInputValue } from '../../lib/format';
import { Field } from '../../components/ui/Field';
import { DatePicker } from '../../components/ui/DatePicker';
import { useCard } from '../creditCard/useCreditCard';

function today(): string {
  return toDateInputValue();
}

/**
 * Da de alta algo que esperas que pase UNA vez: un bono, un seguro anual, un
 * reembolso que te deben. Lo que se repite cada mes no va aquí, eso es un
 * gasto fijo y su pendiente se deduce solo.
 */
export function PlannedForm({
  item,
  onDone,
}: {
  /** Si existe, corrige un pendiente único sin convertirlo en movimiento real. */
  item?: PlannedMovement;
  onDone: () => void;
}) {
  const accountsQ = useAccounts();
  const categoriesQ = useCategories();
  const cardQ = useCard();
  const create = useCreatePlanned();
  const update = useUpdatePlanned();
  const editing = Boolean(item);

  const [kind, setKind] = useState<PlannedMovementKind>(item?.kind ?? PlannedMovementKind.EXPENSE);
  const [amount, setAmount] = useState(item ? String(item.amount) : '');
  const [expectedDate, setExpectedDate] = useState(
    item ? toDateInputValue(item.expectedDate) : today,
  );
  const [accountId, setAccountId] = useState(item?.accountId ?? '');
  const [toAccountId, setToAccountId] = useState(item?.toAccountId ?? '');
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? '');
  const [note, setNote] = useState(item?.note ?? '');
  const [sourceKind, setSourceKind] = useState<RecurringSourceKind>(
    item?.sourceKind ?? RecurringSourceKind.ACCOUNT,
  );

  const accounts = accountsQ.data ?? [];
  const categories = (categoriesQ.data ?? []).filter((c) =>
    kind === PlannedMovementKind.INCOME
      ? c.kind === CategoryKind.INCOME
      : c.kind === CategoryKind.EXPENSE,
  );
  const isTransfer = kind === PlannedMovementKind.TRANSFER;
  const isCardExpense =
    kind === PlannedMovementKind.EXPENSE && sourceKind === RecurringSourceKind.CARD;
  const sameTransferAccount =
    isTransfer && Boolean(accountId && toAccountId && accountId === toAccountId);
  const numericAmount = Number(amount);
  const amountIsValid = amount.trim() !== '' && Number.isFinite(numericAmount) && numericAmount > 0;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const from = accountId;
    if (!amountIsValid || (!isCardExpense && !from)) return;
    if (isTransfer && (!toAccountId || sameTransferAccount)) return;
    const body = {
      amount: numericAmount,
      expectedDate,
      sourceKind: isCardExpense ? RecurringSourceKind.CARD : RecurringSourceKind.ACCOUNT,
      accountId: isCardExpense ? undefined : from,
      toAccountId: isTransfer ? toAccountId : null,
      categoryId: isTransfer ? null : categoryId || null,
      note: note || null,
    };
    if (item) {
      update.mutate({ id: item.id, body }, { onSuccess: onDone });
      return;
    }
    create.mutate({ kind, ...body }, { onSuccess: onDone });
  };

  const tabs = [
    { value: PlannedMovementKind.EXPENSE, label: 'Gasto', active: 'bg-expense text-white' },
    { value: PlannedMovementKind.INCOME, label: 'Ingreso', active: 'bg-income text-white' },
    { value: PlannedMovementKind.TRANSFER, label: 'Traspaso', active: 'bg-savings text-white' },
  ];
  const saving = create.isPending || update.isPending;
  const error = create.isError ? create.error : update.isError ? update.error : undefined;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="rounded-xl bg-amber-50 px-3 py-2.5 text-sm leading-5 text-amber-900">
        {editing
          ? 'Este movimiento único seguirá pendiente: guardar cambios no modifica tus saldos ni la deuda de tu tarjeta. El tipo se conserva; si necesitas cambiarlo, cancélalo y crea otro.'
          : 'Para algo que ocurrirá una sola vez. No modifica tus saldos ni la deuda de tu tarjeta hasta que lo confirmes en Pendientes.'}
      </p>

      {editing ? (
        <div className="rounded-xl bg-slate-100 px-3 py-2.5 text-sm text-slate-600">
          Tipo:{' '}
          <strong className="text-slate-800">
            {tabs.find((tab) => tab.value === kind)?.label}
          </strong>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1">
          {tabs.map((t) => (
            <button
              key={t.value}
              type="button"
              data-form-value-change={kind === t.value ? undefined : 'true'}
              onClick={() => {
                setKind(t.value);
                setCategoryId('');
                if (t.value !== PlannedMovementKind.TRANSFER) setToAccountId('');
                if (t.value !== PlannedMovementKind.EXPENSE) {
                  setSourceKind(RecurringSourceKind.ACCOUNT);
                }
              }}
              className={`tap-target rounded-[9px] py-2 text-sm font-semibold transition ${
                kind === t.value ? `${t.active} shadow-sm` : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {isTransfer && (
        <p className="rounded-xl bg-blue-50 px-3 py-2.5 text-sm leading-5 text-slate-700">
          Mueve dinero entre tus cuentas una sola vez; no es un gasto ni una aportación a una meta.
          Para ahorrar hacia una meta, usa{' '}
          <Link
            to="/metas"
            data-allow-small-target="true"
            className="font-semibold text-savings underline underline-offset-2"
          >
            Aportar dinero
          </Link>{' '}
          y elige un apartado que la respalde.
        </p>
      )}

      <Field
        id="pl-amount"
        label="Monto esperado"
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0.01"
        required
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <DatePicker
        id="pl-date"
        label="Fecha esperada"
        required
        value={expectedDate}
        onChange={setExpectedDate}
        helperText="Se mostrará como pendiente hasta que ocurra."
      />

      {kind === PlannedMovementKind.EXPENSE && (
        <fieldset>
          <legend className="mb-1 block text-sm font-medium text-slate-700">
            ¿Cómo lo pagarás?
          </legend>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-form-value-change={
                sourceKind === RecurringSourceKind.ACCOUNT ? undefined : 'true'
              }
              onClick={() => setSourceKind(RecurringSourceKind.ACCOUNT)}
              className={`tap-target rounded-xl border px-3 py-2.5 text-left text-sm font-semibold transition ${
                sourceKind === RecurringSourceKind.ACCOUNT
                  ? 'border-savings bg-blue-50 text-slate-900 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              Cuenta
            </button>
            <button
              type="button"
              disabled={!cardQ.data}
              data-form-value-change={sourceKind === RecurringSourceKind.CARD ? undefined : 'true'}
              onClick={() => setSourceKind(RecurringSourceKind.CARD)}
              className={`tap-target min-w-0 rounded-xl border px-3 py-2.5 text-left text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                sourceKind === RecurringSourceKind.CARD
                  ? 'border-savings bg-blue-50 text-slate-900 shadow-sm'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              <span className="block truncate">Tarjeta de crédito</span>
              {cardQ.data && (
                <span className="mt-0.5 block truncate text-xs font-normal text-slate-500">
                  {cardQ.data.name}
                </span>
              )}
            </button>
          </div>
          {isCardExpense ? (
            <p className="mt-1.5 text-xs leading-4 text-slate-500">
              Sólo es un pronóstico por ahora. Al confirmarlo se registrará como cargo de contado y
              entonces aumentará la deuda de tu tarjeta.
            </p>
          ) : !cardQ.isLoading && !cardQ.data ? (
            <p className="mt-1.5 text-xs leading-4 text-slate-500">
              Configura una tarjeta primero para planear cargos con ella.
            </p>
          ) : null}
        </fieldset>
      )}

      {!isCardExpense && (
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            {kind === PlannedMovementKind.INCOME ? 'Entra a' : 'Sale de'}
          </span>
          <select
            className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base"
            value={accountId}
            onChange={(e) => {
              const nextAccountId = e.target.value;
              setAccountId(nextAccountId);
              // Si cambiaste el origen por el destino que ya estaba elegido,
              // limpiamos el destino en lugar de dejar un submit que no hace nada.
              if (toAccountId === nextAccountId) setToAccountId('');
            }}
            required
          >
            <option value="" disabled>
              Elige cuenta
            </option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {isTransfer && (
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Llega a</span>
          <select
            className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base"
            value={toAccountId}
            onChange={(e) => setToAccountId(e.target.value)}
            required
          >
            <option value="" disabled>
              Elige cuenta destino
            </option>
            {accounts
              .filter((a) => a.id !== accountId)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
          {sameTransferAccount && (
            <p role="alert" className="mt-1.5 text-xs leading-4 text-expense">
              Elige una cuenta destino diferente al origen.
            </p>
          )}
        </label>
      )}

      {!isTransfer && categories.length > 0 && (
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Categoría</span>
          <select
            className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Sin categoría</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.icon ? `${c.icon} ` : ''}
                {c.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <Field id="pl-note" label="Nota" value={note} onChange={(e) => setNote(e.target.value)} />

      {error && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={
            saving ||
            !amountIsValid ||
            (!isCardExpense && !accountId) ||
            (isTransfer && (!toAccountId || sameTransferAccount))
          }
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-[0_8px_16px_-8px_rgba(15,23,42,.55)] disabled:opacity-60"
        >
          {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear pendiente'}
        </button>
      </div>
    </form>
  );
}
